import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Trophy,
  TrendingUp,
  TrendingDown,
  Target,
  ArrowRight,
  Award,
  BarChart3,
  Sparkles,
  FileText,
  Globe,
  Clock,
  Crown,
  Package
} from 'lucide-react';

// Types matching the backend API response
interface Scores {
  aeo: number;
  seo: number;
  ux: number;
  overall: number;
}

interface ProductData {
  name: string;
  imageUrl: string | null;
  brand: string | null;
}

interface SerpLeaderData {
  title: string;
  url: string;
  position: number;
  score: number;
}

interface Comparison {
  type: 'generated' | 'serp_leader' | 'current_page' | 'competitor';
  label: string;
  pageTitle: string;
  pageUrl: string;
  serpPosition: number | null;
  scores: Scores;
  difference: string;
}

interface RecentBenchmark {
  id: string;
  title: string;
  sourceType: 'product' | 'category';
  sourceId: string;
  serpQuery: string;
  createdAt: string;
  isWinner: boolean;
  verdict: string;
  product: ProductData | null;
  serpLeader: SerpLeaderData | null;
  scores: Scores | null;
  comparisons: Comparison[];
}

interface AEOHighlightsSummary {
  totalComparisons: number;
  wins: number;
  winRate: number;
  avgAeoScore: number;
  avgSeoScore: number;
  avgUxScore: number;
  avgOverallScore: number;
  avgVsSerpLeader: number;
  avgVsSitePage: number;
  beatsSerpLeaderCount: number;
  beatsSitePageCount: number;
}

export interface AEOHighlightsData {
  summary: AEOHighlightsSummary;
  recentBenchmarks: RecentBenchmark[];
  periodDays: number;
}

interface AEOPerformanceHighlightsProps {
  data: AEOHighlightsData | null;
  loading?: boolean;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function getDifferenceDisplay(diff: string | number): { text: string; color: string; icon: React.ReactNode } {
  const numDiff = typeof diff === 'string' ? parseFloat(diff) : diff;
  if (numDiff > 0) {
    return {
      text: `+${numDiff.toFixed(1)}`,
      color: 'text-green-600 dark:text-green-400',
      icon: <TrendingUp className="h-3 w-3" />
    };
  } else if (numDiff < 0) {
    return {
      text: numDiff.toFixed(1),
      color: 'text-red-600 dark:text-red-400',
      icon: <TrendingDown className="h-3 w-3" />
    };
  }
  return {
    text: '0',
    color: 'text-muted-foreground',
    icon: null
  };
}

// Basketball-style scoreboard component
function MatchupScoreboard({ benchmark }: { benchmark: RecentBenchmark }) {
  const ourScore = benchmark.scores?.overall || 0;
  const theirScore = benchmark.serpLeader?.score || 0;
  const isWinning = ourScore > theirScore;

  return (
    <div className="relative bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 rounded-xl p-4 overflow-hidden border border-slate-200 dark:border-slate-700">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-slate-400 dark:bg-white" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 border-2 border-slate-400 dark:border-white rounded-full" />
      </div>

      {/* Winner badge */}
      {benchmark.isWinner && (
        <div className="absolute top-2 left-2">
          <Badge className="bg-amber-500 text-white border-0">
            <Trophy className="h-3 w-3 mr-1" />
            WIN
          </Badge>
        </div>
      )}

      <div className="relative flex items-center justify-between gap-4">
        {/* Home Team - Our AEO Page (Product) */}
        <div className="flex-1 flex flex-col items-center text-center">
          <div className="relative">
            {benchmark.product?.imageUrl ? (
              <img
                src={benchmark.product.imageUrl}
                alt={benchmark.product.name || 'Product'}
                className="w-16 h-16 rounded-xl object-cover border-2 border-amber-400 shadow-lg shadow-amber-400/20"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center ${benchmark.product?.imageUrl ? 'hidden' : ''}`}>
              <Package className="h-8 w-8 text-white" />
            </div>
            {isWinning && (
              <Crown className="absolute -top-2 -right-2 h-5 w-5 text-amber-400 drop-shadow-lg" />
            )}
          </div>
          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-2 uppercase tracking-wide">Your AEO</span>
          <span className="text-xs text-slate-600 dark:text-slate-400 truncate max-w-[100px]" title={benchmark.product?.name || benchmark.title}>
            {benchmark.product?.name?.substring(0, 20) || benchmark.title.substring(0, 20)}
            {(benchmark.product?.name || benchmark.title).length > 20 ? '...' : ''}
          </span>
        </div>

        {/* Scores */}
        <div className="flex items-center gap-3">
          <div className={`text-4xl font-bold tabular-nums ${isWinning ? 'text-green-600 dark:text-green-400' : 'text-slate-700 dark:text-white'}`}>
            {Math.round(ourScore)}
          </div>
          <div className="flex flex-col items-center">
            <span className="text-slate-400 dark:text-slate-500 text-xs uppercase tracking-wider">vs</span>
            <div className="w-8 h-px bg-slate-300 dark:bg-slate-600 my-1" />
          </div>
          <div className={`text-4xl font-bold tabular-nums ${!isWinning ? 'text-green-600 dark:text-green-400' : 'text-slate-700 dark:text-white'}`}>
            {Math.round(theirScore)}
          </div>
        </div>

        {/* Away Team - SERP Leader */}
        <div className="flex-1 flex flex-col items-center text-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-blue-400 shadow-lg shadow-blue-400/20">
              <Globe className="h-8 w-8 text-white" />
            </div>
            {!isWinning && theirScore > ourScore && (
              <Crown className="absolute -top-2 -right-2 h-5 w-5 text-blue-400 drop-shadow-lg" />
            )}
          </div>
          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-2 uppercase tracking-wide">SERP #1</span>
          <span className="text-xs text-slate-600 dark:text-slate-400 truncate max-w-[100px]" title={benchmark.serpLeader?.title || 'SERP Leader'}>
            {(benchmark.serpLeader?.title || 'SERP Leader').substring(0, 20)}
            {(benchmark.serpLeader?.title || '').length > 20 ? '...' : ''}
          </span>
        </div>
      </div>

      {/* Score breakdown bar */}
      <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 mb-1">
          <span>AEO: {benchmark.scores?.aeo.toFixed(0) || 0}</span>
          <span>SEO: {benchmark.scores?.seo.toFixed(0) || 0}</span>
          <span>UX: {benchmark.scores?.ux.toFixed(0) || 0}</span>
        </div>
        <div className="text-xs text-slate-500 text-center">
          {formatRelativeTime(benchmark.createdAt)} • {benchmark.serpQuery && `"${benchmark.serpQuery}"`}
        </div>
      </div>
    </div>
  );
}

export function AEOPerformanceHighlights({ data, loading }: AEOPerformanceHighlightsProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            AEO Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-pulse text-muted-foreground">Loading benchmark data...</div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.summary.totalComparisons === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Trophy className="h-10 w-10 text-muted-foreground mb-3" />
          <h4 className="font-semibold mb-1">No AEO Benchmarks Yet</h4>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Generate AEO content and run benchmarks to see how your pages compare against competitors.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/about-business/offering?tab=categories')}>
            <FileText className="h-4 w-4 mr-2" />
            Create AEO Content
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { summary, recentBenchmarks, periodDays } = data;

  return (
    <div className="space-y-6">
      {/* Header Card with Summary Stats */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:border-amber-900 dark:from-amber-950/30 dark:to-orange-950/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              AEO Performance Highlights
              <Badge variant="outline" className="ml-2 font-normal">
                Last {periodDays} days
              </Badge>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/aeo-benchmarks')}
              className="text-amber-600 hover:text-amber-700 dark:text-amber-400"
            >
              View All Benchmarks
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <CardDescription>
            See how your AI-generated AEO content performs against competitors and SERP leaders
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Summary Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Win Rate */}
            <div className="text-center p-4 rounded-lg bg-white/70 dark:bg-card/70 border">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Award className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Win Rate</span>
              </div>
              <div className={`text-3xl font-bold ${summary.winRate >= 50 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {summary.winRate.toFixed(0)}%
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.wins} of {summary.totalComparisons} won
              </div>
            </div>

            {/* Average Overall Score */}
            <div className="text-center p-4 rounded-lg bg-white/70 dark:bg-card/70 border">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Target className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Avg Score</span>
              </div>
              <div className={`text-3xl font-bold ${getScoreColor(summary.avgOverallScore)}`}>
                {summary.avgOverallScore.toFixed(0)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Overall performance
              </div>
            </div>

            {/* vs SERP Leader */}
            <div className="text-center p-4 rounded-lg bg-white/70 dark:bg-card/70 border">
              <div className="flex items-center justify-center gap-1 mb-1">
                <BarChart3 className="h-4 w-4 text-purple-500" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">vs SERP #1</span>
              </div>
              {(() => {
                const diff = getDifferenceDisplay(summary.avgVsSerpLeader);
                return (
                  <>
                    <div className={`text-3xl font-bold flex items-center justify-center gap-1 ${diff.color}`}>
                      {diff.icon}
                      {diff.text}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Beats {summary.beatsSerpLeaderCount} of {summary.totalComparisons}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* vs Site Page */}
            <div className="text-center p-4 rounded-lg bg-white/70 dark:bg-card/70 border">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Sparkles className="h-4 w-4 text-pink-500" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">vs Current</span>
              </div>
              {(() => {
                const diff = getDifferenceDisplay(summary.avgVsSitePage);
                return (
                  <>
                    <div className={`text-3xl font-bold flex items-center justify-center gap-1 ${diff.color}`}>
                      {diff.icon}
                      {diff.text}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Beats {summary.beatsSitePageCount} of {summary.totalComparisons}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Score Breakdown */}
          <div className="mt-6 pt-4 border-t">
            <h4 className="text-sm font-medium mb-3">Average Scores by Category</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">AEO</span>
                  <span className={getScoreColor(summary.avgAeoScore)}>{summary.avgAeoScore.toFixed(0)}</span>
                </div>
                <Progress value={summary.avgAeoScore} className="h-2" />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">SEO</span>
                  <span className={getScoreColor(summary.avgSeoScore)}>{summary.avgSeoScore.toFixed(0)}</span>
                </div>
                <Progress value={summary.avgSeoScore} className="h-2" />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">UX</span>
                  <span className={getScoreColor(summary.avgUxScore)}>{summary.avgUxScore.toFixed(0)}</span>
                </div>
                <Progress value={summary.avgUxScore} className="h-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Matchups - Basketball Style */}
      {recentBenchmarks.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-muted-foreground" />
              Recent Matchups
            </CardTitle>
            <CardDescription>
              Latest AEO content head-to-head battles against SERP leaders
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {recentBenchmarks.slice(0, 3).map((benchmark) => (
              <MatchupScoreboard key={benchmark.id} benchmark={benchmark} />
            ))}

            {/* View All Button */}
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => navigate('/aeo-benchmarks')}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                View All Benchmarks
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
